/**
 * Molly & Stitch — Wishlist
 * Stores product IDs in localStorage (theme:wishlist) and drives
 * header badge, toggle buttons, drawer, and wishlist page.
 */
(function () {
  'use strict';

  if (!window.themeVariables?.settings?.enableWishlist) return;

  var STORAGE_KEY = 'theme:wishlist';
  var CHANGE_EVENT = 'wishlist:change';
  var settings = window.themeVariables.settings;
  var strings = window.themeVariables.strings || {};

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      return raw.map(Number).filter(function (id) { return id > 0; });
    } catch (e) {
      return [];
    }
  }

  function write(ids) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (e) {
      // Safari private mode
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      detail: { ids: ids, count: ids.length }
    }));
  }

  var Wishlist = {
    get: read,
    count: function () { return read().length; },
    has: function (id) {
      return read().indexOf(Number(id)) !== -1;
    },
    add: function (id) {
      id = Number(id);
      if (!id) return false;
      var ids = read();
      if (ids.indexOf(id) !== -1) return false;
      ids.unshift(id);
      write(ids);
      return true;
    },
    remove: function (id) {
      id = Number(id);
      var ids = read().filter(function (item) { return item !== id; });
      write(ids);
      return true;
    },
    toggle: function (id) {
      if (this.has(id)) {
        this.remove(id);
        return false;
      }
      this.add(id);
      return true;
    },
    clear: function () { write([]); },
    queryString: function (limit) {
      var ids = read().slice(0, limit || 50);
      return ids.map(function (id) { return 'id:' + id; }).join(' OR ');
    }
  };

  window.MSWishlist = Wishlist;

  /* ------------------------------------------------------------------ */
  /* wishlist-button                                                      */
  /* ------------------------------------------------------------------ */
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.productId = Number(this.getAttribute('product-id') || this.dataset.productId);
      this._onClick = this._onClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onChange = this._onChange.bind(this);
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeyDown);
      document.addEventListener(CHANGE_EVENT, this._onChange);
      this._sync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeyDown);
      document.removeEventListener(CHANGE_EVENT, this._onChange);
    }

    _onKeyDown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this._onClick(event);
      }
    }

    _onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.productId) return;
      var added = Wishlist.toggle(this.productId);
      this._sync();
      this.dispatchEvent(new CustomEvent('wishlist:toggled', {
        bubbles: true,
        detail: { productId: this.productId, added: added }
      }));
    }

    _onChange() {
      this._sync();
    }

    _sync() {
      var active = Wishlist.has(this.productId);
      this.classList.toggle('is-active', active);
      this.setAttribute('aria-pressed', active ? 'true' : 'false');
      var label = active
        ? (strings.wishlistRemove || 'Remove from wishlist')
        : (strings.wishlistAdd || 'Add to wishlist');
      this.setAttribute('aria-label', label);
      var sr = this.querySelector('.sr-only');
      if (sr) sr.textContent = label;
      var visibleLabel = this.querySelector('[data-wishlist-label]');
      if (visibleLabel) visibleLabel.textContent = label;
    }
  }

  /* ------------------------------------------------------------------ */
  /* wishlist-dot (header badge)                                          */
  /* ------------------------------------------------------------------ */
  class WishlistDot extends HTMLElement {
    connectedCallback() {
      this._onChange = this._onChange.bind(this);
      document.addEventListener(CHANGE_EVENT, this._onChange);
      this._sync();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this._onChange);
    }

    _onChange(event) {
      this._sync(event.detail && event.detail.count);
    }

    _sync(count) {
      if (typeof count !== 'number') count = Wishlist.count();
      this.classList.toggle('is-visible', count > 0);
      this.setAttribute('data-count', String(count));
      var countEl = this.querySelector('[data-wishlist-count]');
      if (countEl) countEl.textContent = count > 99 ? '99+' : String(count);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Shared product loader                                                */
  /* ------------------------------------------------------------------ */
  async function fetchWishlistSection(sectionId) {
    var q = Wishlist.queryString(50);
    if (!q) return null;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'search?type=product&q=' + encodeURIComponent(q) + '&section_id=' + encodeURIComponent(sectionId);

    try {
      var response = await fetch(url, { priority: 'low' });
      if (!response.ok) return null;
      var html = await response.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('[data-wishlist-results]') || doc.querySelector('.shopify-section') || null;
    } catch (e) {
      return null;
    }
  }

  function bindRemoveButtons(root) {
    root.querySelectorAll('[data-wishlist-remove]').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        var id = Number(btn.getAttribute('data-wishlist-remove'));
        if (id) Wishlist.remove(id);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* wishlist-content (drawer body)                                       */
  /* ------------------------------------------------------------------ */
  class WishlistContent extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('section-id') || 'wishlist-drawer-products';
      this._onChange = this._onChange.bind(this);
      document.addEventListener(CHANGE_EVENT, this._onChange);
      this._render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this._onChange);
    }

    _onChange() {
      this._render();
    }

    async _render() {
      var count = Wishlist.count();
      var drawer = this.closest('.drawer, x-drawer, wishlist-drawer');
      if (drawer) {
        drawer.classList.toggle('drawer--center-body', count === 0);
      }

      var countSlot = document.querySelector('[data-wishlist-header-count]');
      if (countSlot) {
        countSlot.hidden = count === 0;
        countSlot.textContent = count > 0 ? ' (' + count + ')' : '';
      }

      if (count === 0) {
        this.innerHTML =
          '<div class="wishlist-empty">' +
            '<p class="h5 text-center">' + (strings.wishlistEmpty || 'Your wishlist is empty') + '</p>' +
            '<a href="' + (settings.wishlistEmptyLink || '/collections/all') + '" class="button button--outline">' +
              (strings.wishlistEmptyButton || 'Continue shopping') +
            '</a>' +
          '</div>';
        return;
      }

      this.innerHTML = '<div class="wishlist-loading" aria-hidden="true"></div>';
      var results = await fetchWishlistSection(this.sectionId);

      if (!results) {
        this.innerHTML =
          '<div class="wishlist-empty">' +
            '<p class="h5 text-center">' + (strings.wishlistEmpty || 'Your wishlist is empty') + '</p>' +
          '</div>';
        return;
      }

      var content = results.querySelector('[data-wishlist-results]') || results;
      this.replaceChildren(...document.importNode(content, true).childNodes);
      bindRemoveButtons(this);
    }
  }

  /* ------------------------------------------------------------------ */
  /* wishlist-grid (full page)                                            */
  /* ------------------------------------------------------------------ */
  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('section-id') || 'wishlist-page-products';
      this._onChange = this._onChange.bind(this);
      document.addEventListener(CHANGE_EVENT, this._onChange);
      this._render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this._onChange);
    }

    _onChange() {
      this._render();
    }

    async _render() {
      var count = Wishlist.count();
      var emptyEl = this.parentElement && this.parentElement.querySelector('[data-wishlist-page-empty]');
      var countEl = document.querySelector('[data-wishlist-page-count]');

      if (countEl) {
        countEl.hidden = count === 0;
        countEl.textContent = count > 0 ? '(' + count + ')' : '';
      }

      if (count === 0) {
        this.innerHTML = '';
        this.hidden = true;
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      this.hidden = false;
      if (emptyEl) emptyEl.hidden = true;
      this.innerHTML = '<div class="wishlist-loading" aria-hidden="true"></div>';

      var results = await fetchWishlistSection(this.sectionId);
      if (!results) {
        this.innerHTML = '';
        this.hidden = true;
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      var content = results.querySelector('[data-wishlist-results]') || results;
      this.replaceChildren(...document.importNode(content, true).childNodes);
      bindRemoveButtons(this);
    }
  }

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButton);
  }
  if (!customElements.get('wishlist-dot')) {
    customElements.define('wishlist-dot', WishlistDot);
  }
  if (!customElements.get('wishlist-content')) {
    customElements.define('wishlist-content', WishlistContent);
  }
  if (!customElements.get('wishlist-grid')) {
    customElements.define('wishlist-grid', WishlistGrid);
  }

  // Sync any server-rendered buttons after storage is available
  document.addEventListener('DOMContentLoaded', function () {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      detail: { ids: Wishlist.get(), count: Wishlist.count() }
    }));
  });
})();
